import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import LocalStorageService from '@application/common/local-storage-service';
import { initializeAuthFromToken, logout } from '@application/features/auth/authSlice';
import { store } from '@application/store';
import { useAppDispatch, useAppSelector } from '@application/store/useStore';
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { useMsal } from '@azure/msal-react';
import { useEffect, useState } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { Box, CircularProgress, Typography } from '@mui/material';
import NDAModal from '@ui/components/NDAModal';
import collaboratorAPI from '@infrastructure/collaborator-api';
import { fetchCollaboratorNDAStatusAsync } from '@application/features/auth/authThunk';
import i18n from 'i18next';
import { config } from '@application/common/static-config';
const { AUTH_SCOPE } = config;
const ProtectedRoute = () => {
    const { accounts, instance } = useMsal();
    const dispatch = useAppDispatch();
    const location = useLocation();
    const isMsalAuthenticated = accounts.length > 0;
    const account = accounts[0]; // assumes single account
    const { currentUser, isPreviewMode } = useAppSelector((state) => state.auth);
    const accessToken = LocalStorageService.getAccessToken();
    const publicRoutes = ['/login', '/unauthorized'];
    const isPublicRoute = publicRoutes.includes(location.pathname);
    const [isInitialized, setIsInitialized] = useState(false);
    const [isRefreshingMSToken, setIsRefreshingMSToken] = useState(false);
    const [msTokenValid, setMsTokenValid] = useState(true);
    const [ndaDocuments, setNdaDocuments] = useState([]);
    const [collaboratorId, setCollaboratorId] = useState('');
    const [isLoadingNDA, setIsLoadingNDA] = useState(false);
    // Utility function to check if Microsoft token is expired
    const isMSTokenExpired = (token) => {
        if (!token)
            return true;
        try {
            // Decode the JWT token to check expiration
            const payload = JSON.parse(atob(token.split('.')[1]));
            const currentTime = Math.floor(Date.now() / 1000);
            return payload.exp < currentTime;
        }
        catch (error) {
            console.error('Error decoding Microsoft token:', error);
            return true; // If we can't decode it, consider it expired
        }
    };
    // Function to validate and refresh Microsoft token
    const validateAndRefreshMSToken = async () => {
        const msToken = LocalStorageService.getMSAccessToken();
        // If no Microsoft token or token is expired, try to refresh
        if (!msToken || isMSTokenExpired(msToken)) {
            if (isMsalAuthenticated && account) {
                try {
                    const response = await instance.acquireTokenSilent({
                        account,
                        scopes: [AUTH_SCOPE]
                    });
                    LocalStorageService.setMSAccessToken(response.accessToken);
                    return true;
                }
                catch (error) {
                    if (error instanceof InteractionRequiredAuthError) {
                    }
                    // Clear all tokens and redirect to login
                    LocalStorageService.removeAccessToken();
                    LocalStorageService.removeMSAccessToken();
                    store.dispatch(logout());
                    window.location.replace('/login');
                    return false;
                }
            }
            else {
                // Clear all tokens and redirect to login
                LocalStorageService.removeAccessToken();
                LocalStorageService.removeMSAccessToken();
                store.dispatch(logout());
                window.location.replace('/login');
                return false;
            }
        }
        return true;
    };
    useEffect(() => {
        const initializeAuth = async () => {
            // If we have an access token but no currentUser, initialize from token
            if (accessToken && !currentUser) {
                dispatch(initializeAuthFromToken());
            }
            // If we have MSAL account and backend auth, ensure we have a valid MS token
            if (isMsalAuthenticated && account && currentUser && accessToken) {
                const isValid = await validateAndRefreshMSToken();
                if (!isValid) {
                    setMsTokenValid(false);
                    return; // User will be redirected to login
                }
                setMsTokenValid(true);
            }
            else if (currentUser && accessToken) {
                // If we have backend auth but no MSAL account, check if MS token exists and is valid
                const msToken = LocalStorageService.getMSAccessToken();
                if (msToken && !isMSTokenExpired(msToken)) {
                    setMsTokenValid(true);
                }
                else {
                    // MS token is missing or expired, but we have backend auth
                    // This might happen during page reload, so we'll wait for MSAL to initialize
                    console.warn('Microsoft token missing or expired, waiting for MSAL initialization');
                    setMsTokenValid(false);
                }
            }
            setIsInitialized(true);
        };
        initializeAuth();
    }, [isMsalAuthenticated, account, currentUser, accessToken, dispatch, instance]);
    // Check NDA status for Collaborators
    useEffect(() => {
        const checkNDAStatus = async () => {
            const isCollaborator = Array.isArray(currentUser?.role) && (currentUser?.role.includes('Collaborator') || currentUser?.role.includes('TransportManager'));
            if (isCollaborator && !isPreviewMode && currentUser?.id && accessToken) {
                // If isNDASigned is not set, fetch it
                if (currentUser.isNDASigned === undefined) {
                    try {
                        setIsLoadingNDA(true);
                        await dispatch(fetchCollaboratorNDAStatusAsync(currentUser.id));
                    }
                    catch (error) {
                        console.error('Error fetching NDA status:', error);
                    }
                    finally {
                        setIsLoadingNDA(false);
                    }
                }
                // If NDA is not signed, fetch collaborator info and NDA document
                if (currentUser.isNDASigned === false) {
                    try {
                        setIsLoadingNDA(true);
                        const collaboratorResponse = await collaboratorAPI.getCollaboratorByUserId(currentUser.id);
                        const collaborator = collaboratorResponse.data;
                        if (collaborator) {
                            setCollaboratorId(collaborator.id);
                            // Get all NDA documents
                            const ndaDocs = collaborator.collaboratorDocuments?.filter((doc) => doc.type === 10 // NonDisclosureAgreement = 10
                            ) || [];
                            setNdaDocuments(ndaDocs);
                        }
                    }
                    catch (error) {
                        console.error('Error fetching collaborator NDA document:', error);
                    }
                    finally {
                        setIsLoadingNDA(false);
                    }
                }
            }
        };
        if (isInitialized && currentUser && accessToken) {
            checkNDAStatus();
        }
    }, [isInitialized, currentUser, accessToken, dispatch, i18n.language, isPreviewMode]);
    // Effect to check Microsoft token validity on route changes
    useEffect(() => {
        const checkMSToken = async () => {
            // Only check if user is authenticated and we're on a protected route
            if (isMsalAuthenticated && currentUser && accessToken && !isPublicRoute) {
                const msToken = LocalStorageService.getMSAccessToken();
                if (msToken && isMSTokenExpired(msToken)) {
                    // Microsoft token is expired, try to refresh it
                    setIsRefreshingMSToken(true);
                    const isValid = await validateAndRefreshMSToken();
                    setIsRefreshingMSToken(false);
                    if (!isValid) {
                        setMsTokenValid(false);
                        return; // User will be redirected to login
                    }
                    setMsTokenValid(true);
                }
            }
        };
        checkMSToken();
    }, [location.pathname, isMsalAuthenticated, currentUser, accessToken, isPublicRoute]);
    // Don't render anything until we've initialized
    if (!isInitialized) {
        return null; // Or a loading spinner
    }
    // If Microsoft token is invalid and we can't refresh it, redirect to login
    if (!msTokenValid) {
        return _jsx(Navigate, { to: '/login', replace: true });
    }
    // Show loading while refreshing Microsoft token
    if (isRefreshingMSToken) {
        return _jsx("div", { children: "Refreshing authentication..." }); // Or a proper loading component
    }
    // For public routes, only redirect if user is fully authenticated
    if (isPublicRoute) {
        if (isMsalAuthenticated && currentUser && accessToken) {
            return _jsx(Navigate, { to: '/', replace: true });
        }
        return _jsx(Outlet, {});
    }
    // For protected routes, check if user is authenticated
    // Allow access if user has access token
    if (!accessToken) {
        return _jsx(Navigate, { to: '/login', replace: true });
    }
    // If user is Collaborator, check NDA status and restrict routes
    const isCollaborator = Array.isArray(currentUser?.role) && (currentUser?.role.includes('Collaborator') || currentUser?.role.includes('TransportManager'));
    if (isCollaborator && !isPreviewMode) {
        // If NDA is not signed, show modal and prevent navigation
        if (currentUser?.isNDASigned === false && ndaDocuments.length > 0 && collaboratorId) {
            return (_jsxs(_Fragment, { children: [_jsx(NDAModal, { collaboratorId: collaboratorId, ndaDocuments: ndaDocuments, open: true }), _jsx(Box, { sx: { display: 'none' }, children: _jsx(Outlet, {}) })] }));
        }
        // If still loading NDA status, show loading state
        if (isLoadingNDA || currentUser?.isNDASigned === undefined) {
            return (_jsx(Box, { sx: {
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '100vh'
                }, children: _jsxs(Box, { sx: { textAlign: 'center' }, children: [_jsx(CircularProgress, {}), _jsx(Typography, { variant: "body2", sx: { mt: 2 }, children: "Loading..." })] }) }));
        }
        // Restrict to Events and Tasks modules only
        const allowedPrefixes = ['/events', '/event-details', '/create-event', '/tasks', '/event-tasks'];
        const isHome = location.pathname === '/';
        const isAllowed = isHome || allowedPrefixes.some((prefix) => location.pathname.startsWith(prefix));
        if (!isAllowed && !isPublicRoute) {
            return _jsx(Navigate, { to: '/unauthorized', state: { reason: 'unauthorized_access' }, replace: true });
        }
    }
    const userHasAccessToRoute = (pathname, permissions = [], searchParams) => {
        // Special Case
        // For Editing: create-event with eventId query parameter requires Update permission
        // For New Event: Without eventId, it requires Create permission
        if (pathname === '/create-event') {
            const eventId = searchParams?.get('eventId');
            if (eventId) {
                return permissions.includes('Permissions.VdkEvents.Update');
            }
            else {
                return permissions.includes('Permissions.VdkEvents.Create');
            }
        }
        // For Editing: sponsorships/new-sponsorship with edit query parameter requires Update permission
        // For New SponsorShip: Without edit parameter, it requires Create permission
        if (pathname === '/sponsorships/new-sponsorship') {
            const isEdit = searchParams?.get('edit') === '1' || searchParams?.has('id');
            if (isEdit) {
                return permissions.includes('Permissions.Partner.Update');
            }
            else {
                return permissions.includes('Permissions.Partner.Create');
            }
        }
        // Try exact match first
        let requiredPermission = routePermissions[pathname];
        // If no exact match, try matching dynamic routes
        if (!requiredPermission) {
            const pathParts = pathname.split('/');
            const routes = Object.keys(routePermissions);
            // Find matching route pattern
            const matchingRoute = routes.find((route) => {
                const routeParts = route.split('/');
                if (routeParts.length !== pathParts.length)
                    return false;
                return routeParts.every((part, index) => {
                    // Match dynamic parameters (starts with ':') or exact matches
                    return part.startsWith(':') || part === pathParts[index];
                });
            });
            if (matchingRoute) {
                requiredPermission = routePermissions[matchingRoute];
            }
        }
        // If no permission required, allow access
        if (!requiredPermission)
            return true;
        return permissions.includes(requiredPermission);
    };
    // Get search params from location
    const searchParams = new URLSearchParams(location.search);
    if (!userHasAccessToRoute(location.pathname, currentUser?.permissions, searchParams)) {
        return _jsx(Navigate, { to: '/unauthorized', state: { reason: 'unauthorized_access' }, replace: true });
    }
    return _jsx(Outlet, {});
};
export default ProtectedRoute;
export const routePermissions = {
    // Existing permissions
    '/users': 'Permissions.Users.Read',
    '/collaborators': 'Permissions.Collaborator.Read',
    '/roles-permissions': 'Permissions.Roles.Read',
    '/tasks': 'Permissions.VdkTasks.Read',
    '/events': 'Permissions.VdkEvents.Read',
    '/people': 'Permissions.VdkPeople.Read',
    // Additional routes
    '/event-tasks': 'Permissions.VdkTasks.Read',
    '/event-tasks/:eventId': 'Permissions.VdkTasks.Read',
    '/create-event': 'Permissions.VdkEvents.Create',
    '/event-details/:eventId': 'Permissions.VdkEvents.Read',
    '/roles-permissions/:roleId/permissions': 'Permissions.Roles.Read',
    '/people/:id': 'Permissions.VdkPeople.Read',
    '/relations': 'Permissions.ExternalRelation.Read',
    '/relations/:id': 'Permissions.ExternalRelation.Read',
    '/sponsorships': 'Permissions.Partner.Read',
    '/sponsorships/new-sponsorship': 'Permissions.Partner.Create',
    '/sponsorship-details/:id': 'Permissions.Partner.Read',
    '/sponsorship-details/:id/ticket-detail/:eventId': 'Permissions.Partner.Read'
};
